/**
 * Groups transcript entries into turns.
 *
 * A messenger shows bubbles; an agent transcript carries one row per step.
 * Between two of your messages an agent may think, call four tools, report an
 * intermediate result, think again, and only then answer — which as raw rows is
 * a column of bubbles where most carry no conversation at all.
 *
 * So the work an assistant did on the way — its reasoning, its tool calls, and
 * whatever those tools handed back — is folded into `steps`, and every row it
 * actually *spoke* keeps its own bubble. One assistant run therefore yields one
 * group per remark: the steps that led up to it, then the remark. Work that has
 * not led to a remark yet forms a trailing group of its own, so nothing a run
 * did can fall off the end.
 */
import type { Translate } from "../lib/i18n";
import type { ChatEntry } from "./transcript";

export type TurnGroup =
  /** A row that stands on its own: your messages, system notes. */
  | { kind: "single"; id: string; entry: ChatEntry }
  /** One remark by the assistant: what it did first, and what it then said. */
  | { kind: "turn"; id: string; steps: ChatEntry[]; answer: ChatEntry | null };

/** The entry a group is anchored to, for day dividers and ordering. */
export function groupAnchor(group: TurnGroup | undefined): ChatEntry | undefined {
  if (!group) {
    return undefined;
  }
  return group.kind === "single" ? group.entry : (group.steps[0] ?? group.answer ?? undefined);
}

/**
 * A row the assistant said out loud. An image with no caption counts too.
 *
 * Tool output does not, however much text it carries. A Gateway may hand back a
 * result as a `tool` row holding a bare JSON string with no `tool_use` block to
 * pair it against — nothing marks it as machine output except its role. Reading
 * that role as "the agent spoke" turns a five-tool turn into a wall of raw
 * payloads, so the role is what decides, not the presence of text. A picture a
 * tool produced is the exception: it is worth seeing, and the capsule cannot
 * show it.
 */
function isSpoken(entry: ChatEntry): boolean {
  if (entry.role === "tool") {
    return !entry.text && entry.attachments.length > 0;
  }
  return Boolean(entry.text) || entry.attachments.length > 0;
}

/**
 * @param live true while a run is in flight. The rows that landed during it
 *   (`duringRun`) have no answer of their own — the reply is whatever the
 *   streaming bubble ends up with, and drawing the durable rows beside it would
 *   show the same text twice — so they all fold into the steps until the run
 *   settles. Rows that predate the run are grouped normally.
 */
export function groupTurns(entries: readonly ChatEntry[], live = false): TurnGroup[] {
  const groups: TurnGroup[] = [];
  let buffer: ChatEntry[] = [];

  /** The work leading up to one remark, plus the remark. Either may be absent. */
  const pushSegment = (rows: ChatEntry[], answerRow: ChatEntry | null) => {
    const anchor = rows[0] ?? answerRow;
    if (!anchor) {
      return;
    }
    const steps = rows.slice();
    // The speaking row's own tool calls and reasoning happened before it spoke,
    // so they join the steps rather than riding along under the remark.
    if (answerRow && (answerRow.toolActivities.length || answerRow.thinking)) {
      steps.push({ ...answerRow, id: `${answerRow.id}:steps`, text: "", attachments: [] });
    }
    groups.push({
      kind: "turn",
      id: `turn:${anchor.id}`,
      steps,
      answer:
        answerRow && steps.length
          ? { ...answerRow, toolActivities: [], thinking: undefined }
          : answerRow,
    });
  };

  /**
   * A settled run. Every remark keeps its bubble: an agent that reports twice
   * has said two things, and hiding the earlier one inside a collapsed capsule
   * loses a message the person was meant to read.
   */
  const pushTurn = (rows: ChatEntry[]) => {
    let steps: ChatEntry[] = [];
    for (const row of rows) {
      if (isSpoken(row)) {
        pushSegment(steps, row);
        steps = [];
        continue;
      }
      steps.push(row);
    }
    // Reasoning or tool calls trailing the last remark still happened.
    if (steps.length) {
      pushSegment(steps, null);
    }
  };

  /** The turn a run is still working on: nothing settled counts as its answer. */
  const pushLiveTurn = (rows: ChatEntry[]) => {
    groups.push({ kind: "turn", id: `turn:${rows[0].id}`, steps: rows, answer: null });
  };

  const flush = (isLast: boolean) => {
    if (!buffer.length) {
      return;
    }
    const rows = buffer;
    buffer = [];

    if (!live || !isLast) {
      pushTurn(rows);
      return;
    }

    // Only rows that landed during the running turn belong to it. Anything
    // older keeps its answer visible, even though a run is in flight.
    const start = rows.findIndex((entry) => entry.duringRun);
    if (start < 0) {
      pushTurn(rows);
      return;
    }
    if (start > 0) {
      pushTurn(rows.slice(0, start));
    }
    pushLiveTurn(rows.slice(start));
  };

  entries.forEach((entry, index) => {
    if (entry.role === "user" || entry.role === "system") {
      flush(false);
      groups.push({ kind: "single", id: entry.id, entry });
      return;
    }
    buffer.push(entry);
    if (index === entries.length - 1) {
      flush(true);
    }
  });
  flush(true);

  return groups;
}

export type StepSummary = {
  /** Steps as a person would count them: one per tool call, one per remark. */
  count: number;
  /** Distinct tool names, in order, for the collapsed line. */
  labels: string[];
};

export function summarizeSteps(steps: readonly ChatEntry[], t: Translate): StepSummary {
  let count = 0;
  const labels: string[] = [];
  const addLabel = (label: string) => {
    if (!labels.includes(label)) {
      labels.push(label);
    }
  };
  for (const step of steps) {
    if (step.thinking) {
      count += 1;
      addLabel(t("steps.thoughts"));
    }
    for (const activity of step.toolActivities) {
      count += 1;
      addLabel(activity.presentation.title);
    }
    if (step.text.trim()) {
      count += 1;
      if (step.role === "tool") {
        // Otherwise a turn made only of tool payloads reads as "4 Schritte"
        // with nothing to say what they were.
        addLabel(t("steps.toolOutput"));
      }
    }
  }
  return { count, labels };
}
